import { RefundSummary } from '../process-island/summary-tab/refund-summary'
import { formatCurrency } from '@/utils'
import { TextInput, Base } from '@itsrever/design-system'
import { useState } from 'react'
import Edit from '@/assets/images/icons/Edit.svg'
import {
  PbECommerceSettingsUseCaseEnum,
  PbLogisticsSummaryReturnMethodEnum
} from '@itsrever/rever-api'
import { useCreateReviews } from '@/hooks'
import { buildRefundOverride } from '../refund-amount/build-refund-override'
import { useRefundAmountEditor } from '../refund-amount/use-refund-amount-editor'
import Success from '@/assets/images/icons/Success.svg'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'
import { ReturnedItem, ReturnProcess } from '@/domain/returns'
import { Compensation } from '@/domain/refund'
import { NewReview } from '@/domain/review'
import { SelectStoreDialog } from '../select-store-dialog/select-store-dialog'

const {
  Button,
  Card,
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogBackdrop,
  DialogPopup
} = Base

interface RefundIslandProps {
  processDetails: ReturnProcess | undefined
  declinedProducts: ReturnedItem[]
  newReviews: NewReview[]
  setReviewMode: (value: boolean) => void
  returnMethod?: PbLogisticsSummaryReturnMethodEnum
}

const RefundIsland: React.FC<RefundIslandProps> = ({
  processDetails,
  newReviews,
  declinedProducts,
  setReviewMode,
  returnMethod
}) => {
  const { t } = useTranslation()
  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )
  const isReverLite = settings.use_case === PbECommerceSettingsUseCaseEnum.Lite
  const processId = processDetails?.processId ?? ''
  const returnedItems = processDetails?.returnedItems ?? []
  const exchangeItems = processDetails?.orderedItems ?? []
  const extraLineItems = processDetails?.extraLineItems ?? []

  const compensation = processDetails?.compensation ?? ({} as Compensation)

  const userSelectedPM =
    processDetails?.refundPaymentMethod ?? 'NO_REFUND_METHOD'

  const exchangeHasBeenExecuted =
    Number(processDetails?.compensation?.exchange?.executed?.amount ?? 0) > 0

  const totalDeclinedProducts = declinedProducts
    .filter((item) => {
      const isExchange = item.action === 'EXCHANGE'
      if (!isExchange) {
        return true
      }
      return exchangeHasBeenExecuted // if the exchange has been executed and then is marked as missing or declined, it means it is fraud and we should not refund any amount
    })
    .reduce((acc, item) => {
      return (acc += Number(item.unitPrice.amount))
    }, 0)

  const {
    currency,
    calculatedRefundAmount,
    refundAmount,
    setRefundAmount,
    differenceRefundAmount,
    amountEdited: needsReason,
    error,
    hint
  } = useRefundAmountEditor({
    compensation,
    maximumRefundAmount: processDetails?.maximumRefundAmount.amount,
    isReverLite,
    deduction: totalDeclinedProducts
  })

  const [reason, setReason] = useState('')
  const [disabled, setDisabled] = useState(true)

  const {
    mutate: createReview,
    isPending: loadingReview,
    isSuccess: reviewSuccess
  } = useCreateReviews(differenceRefundAmount)

  const onClickRefund = () => {
    createReview({
      processId,
      reviews: newReviews,
      refundOverride: buildRefundOverride(
        needsReason,
        reason,
        refundAmount,
        currency
      )
    })
  }

  const handleClickCancel = () => {
    setReviewMode(false)
  }

  const hasApprovedReview = newReviews.some((r) => r.status === 'APPROVED')

  const showStoreDialog =
    returnMethod === PbLogisticsSummaryReturnMethodEnum.InStore &&
    !!settings.in_store_settings
      ?.automated_store_to_warehouse_logistics_enabled &&
    hasApprovedReview

  return (
    <Card className="h-fit p-5">
      <RefundSummary
        returnedItems={returnedItems}
        exchangeItems={exchangeItems}
        extraLineItems={extraLineItems}
        compensation={compensation}
        userSelectedPM={userSelectedPM}
      />
      {declinedProducts.length > 0 ? (
        <>
          <hr className="mb-2" />
          {declinedProducts.map((prod, i) => {
            return (
              <div className="flex justify-between gap-4" key={i}>
                <p>{prod.product.productName ?? ''}</p>
                <p className="whitespace-nowrap text-red-500">
                  -
                  {formatCurrency(
                    prod.unitPrice.amount ?? 0,
                    prod.unitPrice.currency ?? 'EUR'
                  )}
                </p>
              </div>
            )
          })}
        </>
      ) : null}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm">
          {t('refund_island.refund_amount')} ({currency})
        </p>
        {disabled && newReviews.every((r) => r.status) ? (
          <div
            className="flex cursor-pointer items-center text-grey-3 hover:text-grey-1"
            onClick={() => setDisabled(false)}
          >
            <img src={Edit} className="size-[20px]" />
            <span className="text-xs">{t('refund_island.edit')}</span>
          </div>
        ) : null}
      </div>
      <TextInput
        fullWidth
        hint={hint}
        placeholder={formatCurrency(
          calculatedRefundAmount.toFixed(2),
          currency
        )}
        value={refundAmount}
        onChange={(e) => setRefundAmount(e.currentTarget.value)}
        error={error}
        disabled={disabled}
      />
      {needsReason && !error ? (
        <div className="mt-6">
          <p className="text-sm">{t('refund_island.reason_for_edit')}</p>
          <TextInput
            fullWidth
            value={reason}
            placeholder='e.g. "Box was damaged"'
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </div>
      ) : null}
      <div className="mt-4 flex justify-center">
        {reviewSuccess ? (
          <div className="flex items-center rounded-[5rem] border border-solid border-[rgb(85_181_118)] py-2 pl-3 pr-4 text-sm text-[rgb(85_181_118)]">
            <img className="mr-1 size-6 text-[rgb(85_181_118)]" src={Success} />
            Success
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleClickCancel}>
              {t('products_tab.cancel')}
            </Button>
            {showStoreDialog ? (
              <DialogRoot>
                <DialogTrigger
                  render={
                    <Button
                      loading={loadingReview}
                      variant="primary"
                      disabled={
                        error ||
                        (needsReason && !reason) ||
                        newReviews.some(
                          (r) => !r.status || r.status == 'PENDING'
                        )
                      }
                    />
                  }
                >
                  {t('refund_island.refund')}{' '}
                  {formatCurrency(refundAmount, currency)}
                </DialogTrigger>
                <DialogPortal>
                  <DialogBackdrop />
                  <DialogPopup>
                    <SelectStoreDialog
                      onSubmitStore={onClickRefund}
                      isLoading={loadingReview}
                      customerPrintedOrderId={
                        processDetails?.customerPrintedOrderId
                      }
                      ecommerceId={processDetails?.ecommerceId}
                    />
                  </DialogPopup>
                </DialogPortal>
              </DialogRoot>
            ) : (
              <Button
                variant="primary"
                disabled={
                  error ||
                  (needsReason && !reason) ||
                  newReviews.some((r) => !r.status || r.status == 'PENDING')
                }
                loading={loadingReview}
                onClick={onClickRefund}
              >
                {t('refund_island.refund')}{' '}
                {formatCurrency(refundAmount, currency)}
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

export default RefundIsland
